import { Autocomplete, Box, CircularProgress, IconButton, Paper, TextField, Typography } from '@mui/material'
import { Refresh as RefreshIcon } from '@mui/icons-material'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AsyncActionButton from '@src/shared/components/AsyncActionButton'
import { useSubscriptions, useKeyVaultsList, useRefreshKeyVaults } from '@src/tools/key-vault/api/keyvaults-api'
import { useSecretNames, useValidateSecrets } from '@src/tools/key-vault/api/secrets-api'
import { SecretValidationResult, Subscription } from '@src/tools/key-vault/types/KeyVault'
import ValidateSecretRow from './Components/ValidateSecretRow'

const ValidateSecretsPage = () => {
  const { t } = useTranslation()
  const [subscriptions, isLoadingSubscriptions] = useSubscriptions()
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null)
  const [keyVaults, isLoadingKeyVaults] = useKeyVaultsList(selectedSubscription?.id ?? null)
  const [refreshKeyVaults, isRefreshing] = useRefreshKeyVaults(selectedSubscription?.id ?? null)
  const [validateSecrets, isValidating] = useValidateSecrets()

  const [selectedKeyVault, setSelectedKeyVault] = useState<string | null>(null)
  const [secretValues, setSecretValues] = useState<Record<string, string>>({})
  const [results, setResults] = useState<SecretValidationResult[]>([])

  const [secretNames, isLoadingSecrets] = useSecretNames(selectedKeyVault)

  const handleSubscriptionChange = useCallback((_e: unknown, value: Subscription | null) => {
    setSelectedSubscription(value)
    setSelectedKeyVault(null)
    setSecretValues({})
    setResults([])
  }, [])

  const handleKeyVaultChange = useCallback((_e: unknown, value: string | null) => {
    setSelectedKeyVault(value)
    setSecretValues({})
    setResults([])
  }, [])

  const handleSecretValueChange = useCallback((secretName: string, value: string) => {
    setSecretValues((prev) => ({ ...prev, [secretName]: value }))
    setResults([])
  }, [])

  const handleValidate = async () => {
    if (!selectedKeyVault) return

    const secretsToValidate = Object.entries(secretValues)
      .filter(([, value]) => value.length > 0)
      .map(([key, value]) => ({ key, value }))

    if (secretsToValidate.length === 0) return

    try {
      const response = await validateSecrets({
        keyVaultName: selectedKeyVault,
        secrets: secretsToValidate,
      })
      setResults(response)
    } catch {
      setResults(
        secretsToValidate.map((s) => ({
          secretName: s.key,
          match: false,
        })),
      )
    }
  }

  const hasValues = Object.values(secretValues).some((v) => v.length > 0)

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {t('keyVault.validateSecrets.title')}
      </Typography>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 3 }}>
          <Autocomplete
            options={subscriptions}
            getOptionLabel={(option) => option.name}
            loading={isLoadingSubscriptions}
            value={selectedSubscription}
            onChange={handleSubscriptionChange}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} label={t('keyVault.subscription')} size="small" />
            )}
            sx={{ maxWidth: 400, flex: 1 }}
          />
          <Autocomplete
            options={keyVaults}
            loading={isLoadingKeyVaults || isRefreshing}
            value={selectedKeyVault}
            onChange={handleKeyVaultChange}
            disabled={!selectedSubscription}
            renderInput={(params) => (
              <TextField {...params} label={t('keyVault.keyVaultName')} size="small" />
            )}
            sx={{ maxWidth: 400, flex: 1 }}
          />
          <IconButton
            onClick={() => refreshKeyVaults()}
            disabled={isRefreshing || !selectedSubscription}
            title={t('keyVault.refreshKeyVaults')}
            sx={{ mt: 0.25 }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>

        {isLoadingSecrets && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!isLoadingSecrets && selectedKeyVault && secretNames.length > 0 && (
          <>
            {secretNames.map((name) => (
              <ValidateSecretRow
                key={name}
                secretName={name}
                value={secretValues[name] ?? ''}
                onChange={(value) => handleSecretValueChange(name, value)}
                disabled={isValidating}
                result={results.find((r) => r.secretName === name)}
              />
            ))}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', borderTop: 1, borderColor: 'divider', pt: 2, mt: 2 }}>
              <AsyncActionButton
                variant="contained"
                onClick={handleValidate}
                disabled={!hasValues}
                isPending={isValidating}
                fullWidth={false}
              >
                {t('keyVault.validateSecrets.validate')}
              </AsyncActionButton>
            </Box>
          </>
        )}

        {!isLoadingSecrets && selectedKeyVault && secretNames.length === 0 && (
          <Typography color="text.secondary">
            {t('keyVault.validateSecrets.noSecrets')}
          </Typography>
        )}
      </Paper>
    </Box>
  )
}

export default ValidateSecretsPage
